import React from "react";
import BdrInput from "../../../../components/BdrInput/BdrInput";
import BdrButton from "../../../../components/BdrButton/BdrButton";
import { MyFormHelperText } from "../../../../components/CssTextFields/CssTextFieldStandard";
import phoneNumberFormat from "../../../../utils/formatters/phoneNumberFormat";
import capitalizeFirstLetters from "../../../../utils/formatters/capitalizeFirstLetters";
import "./clientForm.css";

/**
 * ClientFormFields — the client form's FIELDS, and nothing else.
 *
 * Shared by create and edit (`plans/plan_client_form_modal_2026_10_02.md`).
 * Presentational by contract: it holds no state, fires no mutation, opens no
 * modal and performs no navigation. Everything stateful — the Places service,
 * the paste helpers, the referral lookup, the snackbar, the mutations — belongs
 * to `ClientFormModal`, which passes results down.
 *
 * ─────────────────────────────────────────────────────────────────
 * THE RULES THIS FILE EXISTS TO ENFORCE. Each was a review finding.
 *
 * 1. NEVER MUTATE A ROW IN PLACE (§4.4). The code this replaces did
 *    `const arr = [...prev.phoneNumberIds]; arr[i][name] = value;` in two
 *    places — a shallow array copy whose ROW OBJECTS are still shared. That is
 *    harmless today only because the edit page happens to build two independent
 *    maps of the phone list. Share them once and typing a digit rewrites the
 *    client record the form was seeded from. Every change here maps a copy.
 *
 * 2. `_id` DISCIPLINE ON PHONE ROWS (§4.5). An edit row carries its `_id`; the
 *    server relinks by it, and a row that arrives without one is treated as
 *    dropped — `cleanupDroppedPhones` then DELETES the phone document. So
 *    add/remove must preserve `_id` on every surviving row, and a row added in
 *    create mode must NOT invent one.
 *
 * 3. THE NOTE IS TEXT HERE (§4.11). This component edits `value.noteText`, a
 *    plain string. The modal builds the `ClientNoteInput` object at submit with
 *    a save-time `dateCreated`. Create used to stamp that at the first
 *    keystroke, which is why a note could carry a timestamp minutes before the
 *    client it belongs to.
 *
 * 4. "Prev:" LABELS COME FROM `original` (§5 P1), never from `value` — the
 *    whole point is to show what is being changed FROM, so reading the live
 *    form would show the edit back to itself.
 * ─────────────────────────────────────────────────────────────────
 */

const PHONE_TYPES = ["Home", "Cell", "Work", "Spouse", "Other"];
const STATUSES = ["Lead", "Client", "Other"];

/* Fields that get sentence capitalisation as they are typed (BdrInput's
   `autoCapSentences`, the SMS composer's helper). Emails, phone, zip and the
   gate code are deliberately not here: an email must stay lowercase and a code
   is a code. */
const TITLE_CASE_FIELDS = new Set([
  "firstName",
  "lastName",
  "spouseName",
  "streetAddress",
  "city",
  "state",
]);
const EMAIL_FIELDS = new Set(["primaryEmail", "secondaryEmail"]);

/*
 * On-blur hygiene, restored from the form this modal replaced (lost in the
 * 2026-10-02 rebuild): names and address title-cased, emails lowercased with a
 * pasted `mailto:` stripped, zip reduced to digits and a hyphen. Blur, not
 * keystroke, so it never fights the caret.
 */
const tidyField = (name, value) => {
  const text = value ?? "";
  if (TITLE_CASE_FIELDS.has(name)) return capitalizeFirstLetters(text);
  if (EMAIL_FIELDS.has(name)) return text.toLowerCase().replace(/mailto:/gm, "");
  if (name === "zipCode") return text.replace(/[^0-9-]/gi, "");
  return text;
};

const ClientFormFields = ({
  /** "create" | "edit" */
  mode = "create",
  /** the form object; see the shape note in ClientFormModal */
  value,
  /** (nextForm) => void — always receives a NEW object */
  onChange,
  /**
   * The server's current client, for "Prev:" labels. Edit mode only; absent in
   * create, where there is nothing to have been previous.
   */
  original,
  /**
   * Create-from-Messages only: the phone number being ADOPTED. Read-only here —
   * it is displayed so the user can see which number they are attaching, and it
   * must keep reaching the validator as `existingPhn`, or the validator demands
   * a typed row and the adopt path becomes unreachable (§4.5).
   */
  passedPhnObj,
  /** { options, value, isOther, otherText, onSelect, onOtherText } */
  referral,
  /** { suggestions, show, highlight, wrapperRef, onInput, onFocus, onBlur, onKeyDown, onSelect } */
  address,
  onPastePhone,
  onPasteAddress,
  onPasteKeap,
  disabled = false,
}) => {
  const isEdit = mode === "edit";

  /* One place where a field change becomes a new form object. */
  const setField = (name, next) => onChange({ ...value, [name]: next });

  const handleChange = (event) =>
    setField(event.target.name, event.target.value);

  /* Only writes when the tidy actually changed something, so a no-op blur does
     not mint a new form object (the modal's dirty check compares the form). */
  const handleTidyBlur = (event) => {
    const { name, value: current } = event.target;
    const tidy = tidyField(name, current);
    if (tidy !== current) setField(name, tidy);
  };

  /* Rule 1: map a copy. The row object is never written through.
     The number is hyphenated as it is typed (`830-714-1477`) — the formatter
     strips everything but digits and a leading country code first, so a paste
     of "(830) 714 1477" lands the same way. */
  const handlePhoneChange = (event, index) => {
    const { name, value: raw } = event.target;
    const next = name === "phoneNumber" ? phoneNumberFormat(raw ?? "") : raw;
    onChange({
      ...value,
      phoneNumberIds: value.phoneNumberIds.map((row, i) =>
        i === index ? { ...row, [name]: next } : row
      ),
    });
  };

  /* Rule 2: a new row carries no `_id` — the server mints one. Existing rows
     are spread unchanged, so their `_id`s survive. */
  const addPhoneRow = () =>
    setField("phoneNumberIds", [
      ...value.phoneNumberIds,
      { phoneNumber: "", numberType: "", title: "" },
    ]);

  /* Drops the LAST row, matching the control this replaces. Survivors keep
     their `_id` because they are the same objects, untouched. */
  const removePhoneRow = () =>
    setField("phoneNumberIds", value.phoneNumberIds.slice(0, -1));

  /* `undefined` rather than "" so a blank previous value renders no label
     suffix at all, instead of a dangling "Prev: ". */
  const prev = (field, label) => {
    if (!isEdit) return label;
    const was = original?.[field];
    return was ? `${label} — Prev: ${was}` : label;
  };

  return (
    <div className="cform-fields">
      {/* Status is editable in BOTH modes (§3.1). Until now it could be set at
          creation and never changed, so a Lead could never become a Client
          from this page. */}
      <div className="cform-status">
        <span className="cform-status-label">Client Status</span>
        <div className="cform-status-btns">
          {STATUSES.map((status) => (
            <BdrButton
              key={status}
              size="sm"
              type="button"
              disabled={disabled}
              variant={value?.clientStatus === status ? "confirm" : "default"}
              onClick={() => setField("clientStatus", status)}
            >
              {status}
            </BdrButton>
          ))}
        </div>
      </div>

      <BdrInput
        className="cform-f-half"
        type="text"
        label={prev("firstName", "First Name")}
        name="firstName"
        autoComplete="one-time-code"
        autoCapSentences
        disabled={disabled}
        onChange={handleChange}
        onBlur={handleTidyBlur}
        value={value?.firstName || ""}
      />
      <BdrInput
        className="cform-f-half"
        type="text"
        label={prev("lastName", "Last Name")}
        name="lastName"
        autoComplete="one-time-code"
        autoCapSentences
        disabled={disabled}
        onChange={handleChange}
        onBlur={handleTidyBlur}
        value={value?.lastName || ""}
      />
      <BdrInput
        className="cform-f-full"
        type="text"
        label={prev("spouseName", "Spouse's Name")}
        name="spouseName"
        autoComplete="one-time-code"
        autoCapSentences
        disabled={disabled}
        onChange={handleChange}
        onBlur={handleTidyBlur}
        value={value?.spouseName || ""}
      />

      {/* The number being adopted from an unknown-number thread. Read-only on
          purpose: changing it here would silently detach the thread this
          client is being created FOR. */}
      {passedPhnObj?.phoneNumber && (
        <div className="cform-adopted">
          <span className="cform-adopted-label">Attaching number</span>
          <span className="cform-adopted-num">
            {phoneNumberFormat(passedPhnObj.phoneNumber)}
          </span>
        </div>
      )}

      <div className="cform-row-actions">
        <BdrButton
          size="sm"
          type="button"
          variant="action"
          disabled={disabled}
          onClick={onPastePhone}
        >
          Paste Phone
        </BdrButton>
        <BdrButton
          size="sm"
          type="button"
          variant="action"
          disabled={disabled}
          onClick={addPhoneRow}
        >
          Add Phone Number
        </BdrButton>
        {value?.phoneNumberIds?.length > 1 && (
          <BdrButton
            size="sm"
            type="button"
            disabled={disabled}
            onClick={removePhoneRow}
          >
            Remove Number
          </BdrButton>
        )}
      </div>

      {value?.phoneNumberIds?.map((row, index) => (
        <div
          /* Keyed by `_id` where there is one, so an edit's rows keep their
             identity when another is removed. New rows fall back to index —
             they have no id until the server mints one. */
          key={row?._id || `new-phone-${index}`}
          className="cform-phone-row"
        >
          <BdrInput
            className="cform-f-phone"
            type="tel"
            inputMode="numeric"
            label={`Phone Number ${index + 1}`}
            name="phoneNumber"
            autoComplete="one-time-code"
            disabled={disabled}
            onChange={(event) => handlePhoneChange(event, index)}
            value={row?.phoneNumber || ""}
          />
          <BdrInput
            className="cform-f-numtype"
            label="Number Type"
            name="numberType"
            options={PHONE_TYPES}
            disabled={disabled}
            value={row?.numberType || ""}
            onChange={(event) => handlePhoneChange(event, index)}
          />
        </div>
      ))}

      <BdrInput
        className="cform-f-half"
        type="text"
        label={prev("primaryEmail", "Primary Email")}
        name="primaryEmail"
        autoComplete="one-time-code"
        disabled={disabled}
        onChange={handleChange}
        onBlur={handleTidyBlur}
        value={value?.primaryEmail || ""}
      />
      <BdrInput
        className="cform-f-half"
        type="text"
        label="Secondary Email (optional)"
        name="secondaryEmail"
        autoComplete="one-time-code"
        disabled={disabled}
        onChange={handleChange}
        onBlur={handleTidyBlur}
        value={value?.secondaryEmail || ""}
      />

      <div className="cform-row-actions">
        <BdrButton
          size="sm"
          type="button"
          variant="action"
          disabled={disabled}
          onClick={onPasteAddress}
        >
          Paste Address
        </BdrButton>
      </div>

      <div className="cform-address cform-f-half" ref={address?.wrapperRef}>
        <BdrInput
          type="text"
          label={prev("streetAddress", "Street Address")}
          name="streetAddress"
          autoComplete="one-time-code"
          autoCapSentences
          disabled={disabled}
          onChange={(event) => {
            handleChange(event);
            address?.onInput?.(event.target.value);
          }}
          onFocus={() => address?.onFocus?.(value?.streetAddress)}
          onBlur={(event) => {
            handleTidyBlur(event);
            address?.onBlur?.(event);
          }}
          onKeyDown={address?.onKeyDown}
          value={value?.streetAddress || ""}
        />
        {address?.show && address?.suggestions?.length > 0 && (
          <div className="cform-address-dropdown">
            {address.suggestions.map((s, i) => (
              <div
                key={s.place_id}
                className={`cform-address-item${
                  i === address.highlight ? " is-highlighted" : ""
                }`}
                /* mousedown, not click: the field's blur hides this list, and
                   blur lands first on a click. */
                onMouseDown={() => address.onSelect?.(s.place_id)}
              >
                {s.description}
              </div>
            ))}
          </div>
        )}
      </div>

      <BdrInput
        className="cform-f-quarter"
        type="text"
        label="City"
        name="city"
        autoComplete="one-time-code"
        autoCapSentences
        disabled={disabled}
        onChange={handleChange}
        onBlur={handleTidyBlur}
        value={value?.city || ""}
      />
      <BdrInput
        className="cform-f-quarter"
        type="text"
        label="State"
        name="state"
        autoComplete="one-time-code"
        autoCapSentences
        disabled={disabled}
        onChange={handleChange}
        onBlur={handleTidyBlur}
        value={value?.state || ""}
      />
      <BdrInput
        className="cform-f-quarter"
        type="text"
        label="Zip Code"
        name="zipCode"
        maxLength={10}
        inputMode="numeric"
        autoComplete="one-time-code"
        disabled={disabled}
        onChange={handleChange}
        onBlur={handleTidyBlur}
        value={value?.zipCode || ""}
      />
      <BdrInput
        className="cform-f-quarter"
        type="text"
        label={prev("gateCode", "Gate Code")}
        name="gateCode"
        autoComplete="one-time-code"
        disabled={disabled}
        onChange={handleChange}
        value={value?.gateCode || ""}
      />

      <BdrInput
        className="cform-f-half"
        label="How did they hear about us?"
        name="referredBy"
        placeholder="None"
        disabled={disabled}
        options={[
          ...(referral?.options || []).map((opt) => ({
            value: opt._id,
            label: opt.source,
          })),
          { value: "__other__", label: "Other" },
        ]}
        value={
          referral?.isOther ? "__other__" : referral?.value?.sourceOptionId || ""
        }
        onChange={(event) => referral?.onSelect?.(event.target.value)}
      />
      {referral?.isOther && (
        <BdrInput
          className="cform-f-half"
          type="text"
          label="Other - Please specify"
          autoCapSentences
          disabled={disabled}
          value={referral?.otherText || ""}
          onChange={(event) => referral?.onOtherText?.(event.target.value)}
        />
      )}

      <div className="cform-row-actions">
        <BdrButton
          size="sm"
          type="button"
          variant="action"
          disabled={disabled}
          onClick={onPasteKeap}
        >
          Paste Keap Note
        </BdrButton>
      </div>

      <MyFormHelperText
        errorText={
          "Note seems to contain a gate code. Please ensure gate code is entered in correct box and removed from note."
        }
        formError={/gate code|gate:/gi.test(value?.noteText || "")}
      />

      {/* Rule 3: TEXT only. The modal stamps `dateCreated` at save. */}
      <BdrInput
        className="cform-f-full"
        type="text"
        label="Note"
        name="noteText"
        autoCapSentences
        disabled={disabled}
        onChange={handleChange}
        value={value?.noteText || ""}
        multiline
        rows={5}
      />
    </div>
  );
};

export default ClientFormFields;
